/**
 * Hanwha Vision (Wisenet) deep-dive NVR + DVR catalog.
 *
 * Every entry verified against hanwhavisionamerica.com or hanwhavision.com.
 * Each entry cites its datasheet URL. Fields not on the cited page are
 * omitted rather than guessed. Read date: 2026-05-14.
 *
 * Note: 5 IDs overlap with ajaxAndHanwhaCatalog.ts (qrn-820s, prn-3200b2,
 * xrn-3210b4, wrn-1610s, hrx-1632) — the EQUIPMENT_CATALOG Map dedupe
 * handles those (last-spread wins, so this fuller entry takes precedence).
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const HANWHA_DEEP: EquipmentCatalogEntry[] = [
  // Q-SERIES — Compact PoE NVRs ─────────────────────────────────────────
  {
    id: 'hanwha-qrn-410s',
    manufacturer: 'Hanwha Vision', model: 'QRN-410S',
    category: 'nvr-recorder',
    tagline: '4ch Q-series PoE NVR, 8 MP, 4× PoE, 40 Mbps, 1 HDD (6 TB) — discontinued',
    verified: true,
    datasheetUrl: 'https://hanwhavisionamerica.com/product/qrn-410s-discontinued/',
    specs: {
      channels: 4, inputBandwidthMbps: 40, outputBandwidthMbps: 32,
      maxResolution: '8 MP', hddBays: 1, hddBayMaxTb: 6,
      poeInPorts: 4, codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
    },
  },
  {
    id: 'hanwha-qrn-430s',
    manufacturer: 'Hanwha Vision', model: 'QRN-430S',
    category: 'nvr-recorder',
    tagline: '4ch Q-series PoE NVR, 8 MP, 4× PoE (35 W), 40 Mbps, 1U',
    verified: true,
    datasheetUrl: 'https://hanwhavisionamerica.com/product/qrn-430s/',
    specs: {
      channels: 4, inputBandwidthMbps: 40, maxResolution: '8 MP',
      hddBays: 1, hddBayMaxTb: 6, poeInPorts: 4, poeBudgetW: 35,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
      formFactor: '1U', networkPorts: '1× RJ-45 1G WAN + 4× RJ-45 PoE 10/100',
    },
  },
  {
    id: 'hanwha-qrn-820s',
    manufacturer: 'Hanwha Vision', model: 'QRN-820S',
    category: 'nvr-recorder',
    tagline: '8ch Q-series PoE NVR, 8 MP, 8× PoE+ (65 W), 80 Mbps, 1U',
    verified: true,
    datasheetUrl: 'https://hanwhavisionamerica.com/product/qrn-820s/',
    specs: {
      channels: 8, inputBandwidthMbps: 80, outputBandwidthMbps: 32,
      maxResolution: '8 MP', hddBays: 1, hddBayMaxTb: 6,
      poeInPorts: 8, poeBudgetW: 65,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
      formFactor: '1U', networkPorts: '1× RJ-45 1G',
    },
  },
  {
    id: 'hanwha-qrn-830s',
    manufacturer: 'Hanwha Vision', model: 'QRN-830S',
    category: 'nvr-recorder',
    tagline: '8ch Q-series NVR, 8 MP, 8× PoE+ (65 W), 80 Mbps, N+1 failover',
    verified: true,
    datasheetUrl: 'https://hanwhavisionamerica.com/product/qrn-830s/',
    specs: {
      channels: 8, inputBandwidthMbps: 80, outputBandwidthMbps: 32,
      maxResolution: '8 MP', hddBays: 1, hddBayMaxTb: 6,
      poeInPorts: 8, poeBudgetW: 65,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
      formFactor: '1U', networkPorts: '1× RJ-45 1G LAN/WAN',
    },
  },
  {
    id: 'hanwha-qrn-1610s',
    manufacturer: 'Hanwha Vision', model: 'QRN-1610S',
    category: 'nvr-recorder',
    tagline: '16ch Q-series PoE NVR, 8 MP, 16× PoE, 128 Mbps, 2 HDD (12 TB)',
    verified: true,
    datasheetUrl: 'https://hanwhavisionamerica.com/product/qrn-1610s/',
    specs: {
      channels: 16, inputBandwidthMbps: 128, outputBandwidthMbps: 32,
      maxResolution: '8 MP', hddBays: 2, hddBayMaxTb: 6, poeInPorts: 16,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
    },
  },
  {
    id: 'hanwha-qrn-1630s',
    manufacturer: 'Hanwha Vision', model: 'QRN-1630S',
    category: 'nvr-recorder',
    tagline: '16ch Q-series NVR, 8 MP, 16× PoE (130 W), 128 Mbps, 2 HDD (20 TB)',
    verified: true,
    datasheetUrl: 'https://hanwhavisionamerica.com/product/qrn-1630s/',
    specs: {
      channels: 16, inputBandwidthMbps: 128, outputBandwidthMbps: 32,
      maxResolution: '8 MP', hddBays: 2, hddBayMaxTb: 10,
      poeInPorts: 16, poeBudgetW: 130,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
      formFactor: '1U', networkPorts: '1× RJ-45 1G LAN/WAN',
      alarmIO: '4 in / 2 out',
    },
  },

  // X-SERIES — AI NVRs ──────────────────────────────────────────────────
  {
    id: 'hanwha-xrn-410s',
    manufacturer: 'Hanwha Vision', model: 'XRN-410S',
    category: 'nvr-recorder',
    tagline: '4ch X-series PoE NVR, 8 MP, 4× PoE+ (50 W), 50 Mbps, 1U',
    verified: true,
    datasheetUrl: 'https://www.hanwhavisionamerica.com/product/xrn-410s/',
    specs: {
      channels: 4, inputBandwidthMbps: 50, outputBandwidthMbps: 64,
      maxResolution: '8 MP', hddBays: 1, hddBayMaxTb: 6,
      poeInPorts: 4, poeBudgetW: 50,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
      formFactor: '1U', networkPorts: '1× RJ-45 1G + 4× RJ-45 PoE+',
      alarmIO: '4 in / 2 out',
    },
  },
  {
    id: 'hanwha-xrn-810s',
    manufacturer: 'Hanwha Vision', model: 'XRN-810S',
    category: 'nvr-recorder',
    tagline: '8ch X-series PoE NVR, 8 MP, 8× PoE+, 100 Mbps, 2 HDD (12 TB)',
    verified: true,
    datasheetUrl: 'https://www.hanwhavisionamerica.com/product/xrn-810s/',
    specs: {
      channels: 8, inputBandwidthMbps: 100, maxResolution: '8 MP',
      hddBays: 2, hddBayMaxTb: 6, poeInPorts: 8,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
      formFactor: 'desktop',
    },
  },
  {
    id: 'hanwha-xrn-820s',
    manufacturer: 'Hanwha Vision', model: 'XRN-820S',
    category: 'nvr-recorder',
    tagline: '8ch X-series PoE NVR, 32 MP, 8× PoE+ (100 W), 120 Mbps, 2 HDD (12 TB)',
    verified: true,
    datasheetUrl: 'https://www.hanwhavisionamerica.com/product/xrn-820s/',
    specs: {
      channels: 8, inputBandwidthMbps: 120, maxResolution: '32 MP',
      hddBays: 2, hddBayMaxTb: 6, poeInPorts: 8, poeBudgetW: 100,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
      formFactor: '1U', networkPorts: '2× RJ-45 1G + 8× RJ-45 PoE+ 10/100',
      alarmIO: '4 in / 2 out',
    },
  },
  {
    id: 'hanwha-xrn-1610a',
    manufacturer: 'Hanwha Vision', model: 'XRN-1610A',
    category: 'nvr-recorder',
    tagline: '16ch X-series NVR, 12 MP, 180 Mbps, 8 HDD (48 TB), 2U',
    verified: true,
    datasheetUrl: 'https://www.hanwhavision.com/en/products/video-recorder/nvr/ch16/xrn-1610a/',
    specs: {
      channels: 16, inputBandwidthMbps: 180, maxResolution: '12 MP',
      hddBays: 8, hddBayMaxTb: 6,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
      formFactor: '2U', networkPorts: '2× RJ-45 1G',
      alarmIO: '8 in / 4 out',
    },
  },
  {
    id: 'hanwha-xrn-1610sa',
    manufacturer: 'Hanwha Vision', model: 'XRN-1610SA',
    category: 'nvr-recorder',
    tagline: '16ch X-series PoE NVR, 12 MP, 16× PoE+ (220 W), 180 Mbps, 4 HDD (24 TB)',
    verified: true,
    datasheetUrl: 'https://hanwhavisionamerica.com/product/xrn-1610sa/',
    specs: {
      channels: 16, inputBandwidthMbps: 180, outputBandwidthMbps: 32,
      maxResolution: '12 MP', hddBays: 4, hddBayMaxTb: 6,
      poeInPorts: 16, poeBudgetW: 220,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
      formFactor: '2U', networkPorts: '2× RJ-45 1G + 16× RJ-45 PoE/PoE+',
      alarmIO: '8 in / 4 out',
    },
  },
  {
    id: 'hanwha-xrn-1620b2',
    manufacturer: 'Hanwha Vision', model: 'XRN-1620B2',
    category: 'nvr-recorder',
    tagline: '16ch X-series NVR, 32 MP, 140 Mbps, 8 HDD (80 TB), N+1',
    verified: true,
    datasheetUrl: 'https://www.hanwhavisionamerica.com/product/xrn-1620b2/',
    specs: {
      channels: 16, inputBandwidthMbps: 140, maxResolution: '32 MP',
      hddBays: 8, hddBayMaxTb: 10,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
      formFactor: '2U', networkPorts: '2× RJ-45 1G',
      alarmIO: '4 in / 2 out',
    },
  },
  {
    id: 'hanwha-xrn-1620sb1',
    manufacturer: 'Hanwha Vision', model: 'XRN-1620SB1',
    category: 'nvr-recorder',
    tagline: '16ch X-series PoE NVR, 32 MP, 16× PoE+ (200 W), 140 Mbps, 4 HDD (24 TB)',
    verified: true,
    datasheetUrl: 'https://www.hanwhavisionamerica.com/product/xrn-1620sb1/',
    specs: {
      channels: 16, inputBandwidthMbps: 140, maxResolution: '32 MP',
      hddBays: 4, hddBayMaxTb: 6, poeInPorts: 16, poeBudgetW: 200,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
      formFactor: '2U', networkPorts: '2× RJ-45 1G + 16× RJ-45 PoE+ 10/100',
      alarmIO: '4 in / 2 out',
    },
  },
  {
    id: 'hanwha-xrn-3210b2',
    manufacturer: 'Hanwha Vision', model: 'XRN-3210B2',
    category: 'nvr-recorder',
    tagline: '32ch X-series NVR, 32 MP, 400 Mbps, 8 HDD, ARB + N+1 failover',
    verified: true,
    datasheetUrl: 'https://www.hanwhavisionamerica.com/product/xrn-3210b2/',
    specs: {
      channels: 32, inputBandwidthMbps: 400, maxResolution: '32 MP',
      hddBays: 8, codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
      formFactor: '2U',
    },
  },
  {
    id: 'hanwha-xrn-3210b4',
    manufacturer: 'Hanwha Vision', model: 'XRN-3210B4',
    category: 'nvr-recorder',
    tagline: '32ch X-series AI NVR, 32 MP, 400 Mbps, 16 HDD (160 TB), RAID 5/6',
    verified: true,
    datasheetUrl: 'https://www.hanwhavisionamerica.com/product/xrn-3210b4/',
    specs: {
      channels: 32, inputBandwidthMbps: 400, outputBandwidthMbps: 64,
      maxResolution: '32 MP', hddBays: 16, hddBayMaxTb: 10,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      raidLevels: ['5', '6'], onvif: true,
      formFactor: '3U', networkPorts: '3× RJ-45 1G',
      alarmIO: '8 in / 4 out',
    },
  },
  {
    id: 'hanwha-xrn-3220b2',
    manufacturer: 'Hanwha Vision', model: 'XRN-3220B2',
    category: 'nvr-recorder',
    tagline: '32ch X-series AI NVR, 32 MP, 520 Mbps record / 200 Mbps playback, 8 HDD, RAID 5/6',
    verified: true,
    datasheetUrl: 'https://www.hanwhavisionamerica.com/product/xrn-3220b2/',
    specs: {
      channels: 32, inputBandwidthMbps: 520, outputBandwidthMbps: 200,
      maxResolution: '32 MP', hddBays: 8, hddBayMaxTb: 10,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      raidLevels: ['5', '6'], onvif: true,
    },
  },
  {
    id: 'hanwha-xrn-6410b2',
    manufacturer: 'Hanwha Vision', model: 'XRN-6410B2',
    category: 'nvr-recorder',
    tagline: '64ch X-series NVR, 32 MP, 400 Mbps, 8 HDD (80 TB), N+1 failover',
    verified: true,
    datasheetUrl: 'https://www.hanwhavisionamerica.com/product/xrn-6410b2/',
    specs: {
      channels: 64, inputBandwidthMbps: 400, maxResolution: '32 MP',
      hddBays: 8, hddBayMaxTb: 10,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
      formFactor: '2U', networkPorts: '3× RJ-45 1G',
      alarmIO: '8 in / 4 out',
    },
  },
  {
    id: 'hanwha-xrn-6410b4',
    manufacturer: 'Hanwha Vision', model: 'XRN-6410B4',
    category: 'nvr-recorder',
    tagline: '64ch X-series AI NVR, 32 MP, 400 Mbps, 16 HDD (160 TB), RAID 5/6',
    verified: true,
    datasheetUrl: 'https://www.hanwhavision.com/en/products/video-recorder/nvr/ch64/xrn-6410b4/',
    specs: {
      channels: 64, inputBandwidthMbps: 400, outputBandwidthMbps: 64,
      maxResolution: '32 MP', hddBays: 16, hddBayMaxTb: 10,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      raidLevels: ['5', '6'], onvif: true,
      formFactor: '3U', networkPorts: '3× RJ-45 1G',
      alarmIO: '8 in / 4 out',
    },
  },
  {
    id: 'hanwha-xrn-6410db4',
    manufacturer: 'Hanwha Vision', model: 'XRN-6410DB4',
    category: 'nvr-recorder',
    tagline: '64ch X-series Decoder NVR, 32 MP, 400 Mbps, 16 HDD (160 TB), RAID 5/6',
    verified: true,
    datasheetUrl: 'https://www.hanwhavisionamerica.com/product/xrn-6410db4/',
    specs: {
      channels: 64, inputBandwidthMbps: 400, maxResolution: '32 MP',
      hddBays: 16, hddBayMaxTb: 10,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      raidLevels: ['5', '6'], onvif: true,
      formFactor: '3U', networkPorts: '3× RJ-45 1G',
      alarmIO: '8 in / 4 out',
    },
  },
  {
    id: 'hanwha-xrn-6420b2',
    manufacturer: 'Hanwha Vision', model: 'XRN-6420B2',
    category: 'nvr-recorder',
    tagline: '64ch X-series AI NVR, 32 MP, 520 Mbps record / 200 Mbps playback, 8 HDD, RAID 5/6',
    verified: true,
    datasheetUrl: 'https://www.hanwhavisionamerica.com/product/xrn-6420b2/',
    specs: {
      channels: 64, inputBandwidthMbps: 520, outputBandwidthMbps: 200,
      maxResolution: '32 MP', hddBays: 8, hddBayMaxTb: 10,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      raidLevels: ['5', '6'], onvif: true,
    },
  },

  // P-SERIES — Enterprise NVRs ──────────────────────────────────────────
  {
    id: 'hanwha-prn-3200b2',
    manufacturer: 'Hanwha Vision', model: 'PRN-3200B2',
    category: 'nvr-recorder',
    tagline: '32ch P-series enterprise NVR, 32 MP, 400 Mbps, 8 HDD (80 TB), RAID 5/6, 2U',
    verified: true,
    datasheetUrl: 'https://www.hanwhavision.com/en/products/video-recorder/nvr/ch32/prn-3200b2/',
    specs: {
      channels: 32, inputBandwidthMbps: 400, outputBandwidthMbps: 64,
      maxResolution: '32 MP', hddBays: 8, hddBayMaxTb: 10,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      raidLevels: ['5', '6'], onvif: true,
      formFactor: '2U', networkPorts: '3× RJ-45 1G',
      alarmIO: '8 in / 4 out',
    },
  },
  {
    id: 'hanwha-prn-3200b4',
    manufacturer: 'Hanwha Vision', model: 'PRN-3200B4',
    category: 'nvr-recorder',
    tagline: '32ch P-series AI NVR, 32 MP, 400 Mbps, 16 HDD (160 TB), RAID 5/6, 3U',
    verified: true,
    datasheetUrl: 'https://hanwhavisionamerica.com/product/prn-3200b4/',
    specs: {
      channels: 32, inputBandwidthMbps: 400, outputBandwidthMbps: 64,
      maxResolution: '32 MP', hddBays: 16, hddBayMaxTb: 10,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      raidLevels: ['5', '6'], onvif: true,
      formFactor: '3U', networkPorts: '3× RJ-45 1G',
      alarmIO: '8 in / 4 out',
    },
  },
  {
    id: 'hanwha-prn-6400b4',
    manufacturer: 'Hanwha Vision', model: 'PRN-6400B4',
    category: 'nvr-recorder',
    tagline: '64ch P-series AI NVR, 32 MP, 16 HDD (160 TB), AI Best-Shot search',
    verified: true,
    datasheetUrl: 'https://www.hanwhavision.com/en/products/video-recorder/nvr/ch64/prn-6400b4/',
    specs: {
      channels: 64, maxResolution: '32 MP',
      hddBays: 16, hddBayMaxTb: 10,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
    },
  },

  // WAVE (WRN) — Linux WAVE-VMS PoE+ NVRs ───────────────────────────────
  {
    id: 'hanwha-wrn-810s',
    manufacturer: 'Hanwha Vision', model: 'WRN-810S',
    category: 'nvr-recorder',
    tagline: '8ch WAVE PoE+ NVR, 8× PoE+ (100 W), 2 HDD (12 TB), 2× 1G uplink — discontinued',
    verified: true,
    datasheetUrl: 'https://hanwhavisionamerica.com/product/wrn-810s/',
    specs: {
      channels: 8, hddBays: 2, hddBayMaxTb: 6,
      poeInPorts: 8, poeBudgetW: 100,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
      networkPorts: '2× RJ-45 1G uplink + 8× RJ-45 PoE+',
    },
  },
  {
    id: 'hanwha-wrn-816s',
    manufacturer: 'Hanwha Vision', model: 'WRN-816S',
    category: 'nvr-recorder',
    tagline: '8ch WAVE PoE+ NVR, 8× PoE+ (100 W), 150 Mbps, up to 20 TB, AI metadata',
    verified: true,
    datasheetUrl: 'https://hanwhavisionamerica.com/product/wrn-816s/',
    specs: {
      channels: 8, inputBandwidthMbps: 150,
      hddBays: 2, hddBayMaxTb: 10,
      poeInPorts: 8, poeBudgetW: 100, onvif: true,
    },
  },
  {
    id: 'hanwha-wrn-1610s',
    manufacturer: 'Hanwha Vision', model: 'WRN-1610S',
    category: 'nvr-recorder',
    tagline: '16ch WAVE PoE+ NVR, 16× PoE+ (200 W), 4 HDD (24 TB), Ubuntu Linux',
    verified: true,
    datasheetUrl: 'https://hanwhavisionamerica.com/product/wrn-1610s/',
    specs: {
      channels: 16, hddBays: 4, hddBayMaxTb: 6,
      poeInPorts: 16, poeBudgetW: 200,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
    },
  },
  {
    id: 'hanwha-wrn-1632s',
    manufacturer: 'Hanwha Vision', model: 'WRN-1632S',
    category: 'nvr-recorder',
    tagline: '16ch WAVE PoE+ NVR, 16× PoE+ (200 W), 250 Mbps, up to 40 TB, AI metadata',
    verified: true,
    datasheetUrl: 'https://hanwhavisionamerica.com/product/wrn-1632s/',
    specs: {
      channels: 16, inputBandwidthMbps: 250,
      hddBays: 4, hddBayMaxTb: 10,
      poeInPorts: 16, poeBudgetW: 200, onvif: true,
    },
  },

  // HRX — Pentabrid DVRs (AHD/HD-TVI/HDCVI/CVBS/IP) ─────────────────────
  {
    id: 'hanwha-hrx-435',
    manufacturer: 'Hanwha Vision', model: 'HRX-435',
    category: 'nvr-recorder',
    tagline: '4-analog + 2-IP pentabrid DVR, AHD/TVI/CVI/CVBS/IP, 8 MP, 2 HDD',
    verified: true,
    datasheetUrl: 'https://hanwhavisionamerica.com/product/hrx-435/',
    specs: {
      channels: 6, maxResolution: '8 MP',
      hddBays: 2, hddBayMaxTb: 6,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
      hybrid: true,
    },
  },
  {
    id: 'hanwha-hrx-835',
    manufacturer: 'Hanwha Vision', model: 'HRX-835',
    category: 'nvr-recorder',
    tagline: '8-analog + 8-IP pentabrid DVR, 8 MP, 50 Mbps, 4 HDD',
    verified: true,
    datasheetUrl: 'https://hanwhavisionamerica.com/product/hrx-835/',
    specs: {
      channels: 16, inputBandwidthMbps: 50, maxResolution: '8 MP',
      hddBays: 4, hddBayMaxTb: 6,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
      formFactor: '1U', networkPorts: '1× RJ-45 1G',
      alarmIO: '8 in / 4 out', hybrid: true,
    },
  },
  {
    id: 'hanwha-hrx-835a',
    manufacturer: 'Hanwha Vision', model: 'HRX-835A',
    category: 'nvr-recorder',
    tagline: '8-analog + 10-IP pentabrid DVR, 8 MP, 80 Mbps, 4 HDD',
    verified: true,
    datasheetUrl: 'https://hanwhavisionamerica.com/product/hrx-835a/',
    specs: {
      channels: 18, inputBandwidthMbps: 80, maxResolution: '8 MP',
      hddBays: 4, hddBayMaxTb: 6,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
      formFactor: '1U', networkPorts: '1× RJ-45 1G',
      alarmIO: '8 in / 4 out', hybrid: true,
    },
  },
  {
    id: 'hanwha-hrx-1632',
    manufacturer: 'Hanwha Vision', model: 'HRX-1632',
    category: 'nvr-recorder',
    tagline: '16-analog + 8-IP pentabrid DVR, 4 MP analog / 8 MP IP, 80 Mbps, 4 HDD',
    verified: true,
    datasheetUrl: 'https://hanwhavisionamerica.com/product/hrx-1632/',
    specs: {
      channels: 24, inputBandwidthMbps: 80, maxResolution: '8 MP',
      hddBays: 4, hddBayMaxTb: 6,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
      formFactor: '1U', networkPorts: '1× RJ-45 1G',
      alarmIO: '16 in / 4 out', hybrid: true,
    },
  },
  {
    id: 'hanwha-hrx-1634',
    manufacturer: 'Hanwha Vision', model: 'HRX-1634',
    category: 'nvr-recorder',
    tagline: '16-analog + 2-IP pentabrid DVR, 8 MP, 128 Mbps, 2 HDD (12 TB)',
    verified: true,
    datasheetUrl: 'https://hanwhavisionamerica.com/product/hrx-1634/',
    specs: {
      channels: 18, inputBandwidthMbps: 128, maxResolution: '8 MP',
      hddBays: 2, hddBayMaxTb: 6,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
      formFactor: '1U', networkPorts: '1× RJ-45 1G',
      alarmIO: '16 in / 4 out', hybrid: true,
    },
  },
  {
    id: 'hanwha-hrx-1635',
    manufacturer: 'Hanwha Vision', model: 'HRX-1635',
    category: 'nvr-recorder',
    tagline: '16-analog + 2-IP pentabrid DVR, 8 MP, 128 Mbps, 8 HDD (48 TB), 1U',
    verified: true,
    datasheetUrl: 'https://hanwhavisionamerica.com/product/hrx-1635/',
    specs: {
      channels: 18, inputBandwidthMbps: 128, outputBandwidthMbps: 32,
      maxResolution: '8 MP', hddBays: 8, hddBayMaxTb: 6,
      codecs: ['H.264', 'H.265', 'MJPEG'], onvif: true,
      formFactor: '1U', networkPorts: '1× RJ-45 1G',
      alarmIO: '16 in / 4 out', hybrid: true,
    },
  },

  // SRN — Legacy / discontinued ─────────────────────────────────────────
  {
    id: 'hanwha-srn-873s',
    manufacturer: 'Hanwha Vision', model: 'SRN-873S',
    category: 'nvr-recorder',
    tagline: '8ch legacy PoE+ NVR, 8 MP, 64 Mbps, 8× PoE/PoE+, 2 HDD — discontinued',
    verified: true,
    datasheetUrl: 'https://www.hanwhavision.com/en/products/video-recorder/nvr/ch8/srn-873s/',
    specs: {
      channels: 8, inputBandwidthMbps: 64, maxResolution: '8 MP',
      hddBays: 2, poeInPorts: 8,
      codecs: ['H.264', 'MJPEG'], onvif: true,
    },
  },
  {
    id: 'hanwha-srn-1000',
    manufacturer: 'Hanwha Vision', model: 'SRN-1000',
    category: 'nvr-recorder',
    tagline: '64ch legacy NVR, 5 MP, 100 Mbps, 8 HDD, H.264 — discontinued',
    verified: true,
    datasheetUrl: 'https://www.hanwhavision.com/en/products/video-recorder/nvr/ch64/srn-1000/',
    specs: {
      channels: 64, inputBandwidthMbps: 100, maxResolution: '5 MP',
      hddBays: 8, codecs: ['H.264', 'MJPEG'], onvif: true,
    },
  },
];
